/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IGroup, IGroupDelivery, IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/common';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import { IOrderConfirmationProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';

/* VSI Customization - START - 23/11/20 */
export interface ISalesInfoProps {
    itemCount: React.ReactElement;
    grossAmount: React.ReactElement;
}
export interface IOrderDate {
    dateLabel: string;
    orderCreatedDate: string;
}
export interface IOrderSummaryProduct {
    products: SimpleProduct[] | undefined;
    formattedPrice: string | undefined;
    clubPlusText: string;
    clubPlusTrialText: string;
}
/* VSI Customization - END */

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation & ISalesInfoProps & IOrderDate> = props => {
    const { orderInformationProps, channelReferenceId, itemCount, grossAmount, dateLabel, orderCreatedDate } = props;

    return (
        <Node {...orderInformationProps}>
            {/* VSI Customization - START - 23/11/20 */}
            <Node className='__order-brief-info' tag='div'>
                <Node className='__order-information-created-date' tag='span'>
                    <Node className='__created-date' tag='span'>
                        <strong>{dateLabel}</strong>
                    </Node>
                    <Node className='__created-date-label' tag='span'>
                        {orderCreatedDate}
                    </Node>
                </Node>
                {itemCount}
                {grossAmount}
            </Node>
            {/* VSI Customization - END - 27/11/20 */}
            {channelReferenceId}
        </Node>
    );
};

/* VSI Customization - START - 23/11/20 - Removed OrderConfirmationSalesLine FC -  END */

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, address }) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = props => {
    const { groupProps, delivery } = props;
    return (
        <Node {...groupProps}>
            {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
            {/* VSI Customization - START - 23/11/20 - Displayed sales line in OrderConfirmationGroups function and removed from here - VSI Customization - END */}
        </Node>
    );
};
/* VSI Customization - START - 23/11/20 */
// Converted functional component to simple function to pass order confirmation props to it
const OrderConfirmationGroups = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>) => (
    <>
        {props.groups && (
            <Node {...props.groups.groupsProps}>
                {props.groups.groups.map((group, index) => {
                    const { salesLines, salesLinesProps } = group;
                    return (
                        <>
                            {salesLines && (
                                <Node {...salesLinesProps}>
                                    {index === 0 && <OrderConfirmationGroup {...group} />}
                                    {props.slots.consentHelpText && props.slots.consentHelpText}
                                </Node>
                            )}
                            {renderLinks(props)}
                        </>
                    );
                })}
            </Node>
        )}
    </>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary & IOrderSummaryProduct> = ({
    orderSummaryProps,
    heading,
    shipping,
    totalAmount,
    earnedPoints,
    formattedPrice,
    clubPlusTrialText,
    clubPlusText
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        <div>
            <span>{formattedPrice === '£0.00' || formattedPrice === '£0.01' ? clubPlusTrialText : clubPlusText}</span>
            <span>{formattedPrice}</span>
        </div>
        {shipping}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {/* VSI-Customization -- START
        Payment not required on order-confirmation page as per design
        */}
        {title}
        {methods}
        {/* VSI-Customization -- END */}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);
/* VSI Customization - START - 23/11/20 */
const _getCountandPrice = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>): ISalesInfoProps => {
    const {
        data: { orderHydration },
        resources: { itemLabel, itemsLabel, orderTotalLabelText }
    } = props;
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    // items count
    let itemsCount = 0;
    salesOrder &&
        salesOrder.SalesLines &&
        salesOrder.SalesLines.map(salesLine => {
            itemsCount = salesLine.Quantity ? itemsCount + salesLine.Quantity : itemsCount;
        });
    // format price
    const grossAmount = salesOrder && salesOrder.GrossAmount;
    const totalAmount = grossAmount && props.context.cultureFormatter.formatCurrency(grossAmount.toString(), props.context.request.channel?.Currency);
    return {
        itemCount: (
            <Node className='__sales-order-count' tag='div'>
                <Node className='label' tag='span'>
                    {itemsCount}
                </Node>
                <Node className='value' tag='span'>
                    {itemsCount === 1 ? itemLabel : itemsLabel}
                </Node>
            </Node>
        ),
        grossAmount: (
            <Node className='__sales-order-total' tag='div'>
                <Node className='label' tag='span'>
                    {orderTotalLabelText}
                </Node>
                <Node className='value' tag='span'>
                    {totalAmount}
                </Node>
            </Node>
        )
    };
};

/* VSI Customization - END */

const renderLinks = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>) => {
    const {
        config: { shopNowLink, myAccount },
        data: { orderHydration }
    } = props;
    let showShopNowLink = true;
    const products = orderHydration && orderHydration.result && orderHydration.result.products;
    const boltonAttr = props.data.accountInformation?.result?.Attributes?.find(
        attr => attr.Name?.toLowerCase() === 'IsBoltonMember'.toLowerCase());
    if (boltonAttr && boltonAttr.AttributeValue?.StringValue === 'Yes' || (products && products.length && (products[0].RecordId === props.app.config.ClubBoltOnTierId || products[0].RecordId === props.app.config.ClubBoltOnTierIdSecond))) {
        showShopNowLink = false;
    }
    return (
        <div className='ms-dobbies-account-shop-links'>
            <a className='ms-dobbies-account-link' aria-label={myAccount?.ariaLabel} href={myAccount?.linkUrl.destinationUrl}>
                {myAccount?.linkText}
            </a>
            {showShopNowLink ? <a className='ms-dobbies-shop-link' aria-label={shopNowLink?.ariaLabel} href={shopNowLink?.linkUrl.destinationUrl}>
                {shopNowLink?.linkText}
            </a> : <></>}
        </div>
    );
};

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<{}>> = props => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        slots: { helpCentreBlock },
        data: { orderHydration },
        resources: { orderPlacedOnLabel, clubPlusText, clubPlusTrialText }
    } = props;
    /* VSI Customization - START - 23/11/20 */
    const salesOrderInfo = _getCountandPrice(props);
    const salesOrder = orderHydration && orderHydration.result && orderHydration.result.salesOrder;
    const products = orderHydration && orderHydration.result && orderHydration.result.products;
    const [boltonProductTitle, setBoltonProductTitle] = React.useState<string | undefined>('');
    const createdDateTime = salesOrder && salesOrder.SalesLines && salesOrder.SalesLines[0] && salesOrder.SalesLines[0].SalesDate;
    const locale = get(props, 'context.request.locale') || 'en-US';
    const price = props.data.orderHydration.result?.products?.[0].Price;
    let formattedPrice;
    const orderCreatedDate = (createdDateTime && new Date(createdDateTime).toLocaleDateString(locale)) || '';

    if (price === 0) {
        formattedPrice = '£0.00';
    } else {
        formattedPrice = price && props.context.cultureFormatter.formatCurrency(price, props.context.request.channel?.Currency);
    }
    //setCookies for half n hour After upgrade loyalty
    let isLoyaltyInProgress: string = props.context.request.user.customerAccountNumber!;
    if (isLoyaltyInProgress && !props.context.request.cookies.get(isLoyaltyInProgress).value) {
        props.context.request.cookies.set(isLoyaltyInProgress, true, { maxAge: 3600 });
    }
    const created = {
        dateLabel: orderPlacedOnLabel,
        orderCreatedDate: orderCreatedDate
    };

    React.useEffect(() => {
        const boltonAttr = props.data.accountInformation?.result?.Attributes?.find(
            attr => attr.Name?.toLowerCase() === 'IsBoltonMember'.toLowerCase());
        if ((boltonAttr && boltonAttr.AttributeValue?.StringValue === 'Yes') || (products && products.length && (products[0].RecordId === props.app.config.ClubBoltOnTierId || products[0].RecordId === props.app.config.ClubBoltOnTierIdSecond))) {
            const ocHeading = document.getElementsByClassName('order-confirmation__heading');
            if (ocHeading && ocHeading.length) {
                const ps = ocHeading[0].getElementsByTagName('p');
                if (ps && ps.length) {
                    ps[0].innerHTML = props.config.boltonsThankyouSubHeading ? props.config.boltonsThankyouSubHeading : 'You’re now supercharged! Your Double Up benefits will begin on 1st October 2024';
                }                
            }

            const detailMsgs = document.getElementsByClassName('club-plus-oc-detail-msg');
            if (detailMsgs && detailMsgs.length) {
                detailMsgs[0].innerHTML = props.config.boltonsDetailMsg ? props.config.boltonsDetailMsg : 'An additional two drink credits will be automatically added to your membership card on the 1st of each month throughout the offer period. See full terms and conditions here: https://www.dobbies.com/club-terms-and-conditions';
            }

            const descHeading = document.getElementsByClassName('ms-order-confirmation__group-delivery-heading');
            if (descHeading && descHeading.length) {
                descHeading[0].innerHTML = props.config.boltonsDetailMsgHeading ? props.config.boltonsDetailMsgHeading : 'How do I redeem my extra hot drinks';
            }
            // Update Order Summary Product Title
        }
    }, [boltonProductTitle])

    React.useEffect(() => {
        // setTimeout(() => {
            const boltonAttr = props.data.accountInformation?.result?.Attributes?.find(
                attr => attr.Name?.toLowerCase() === 'IsBoltonMember'.toLowerCase());
            if (boltonAttr && boltonAttr.AttributeValue?.StringValue === 'Yes' || (products && products.length && (products[0].RecordId === props.app.config.ClubBoltOnTierId || products[0].RecordId === props.app.config.ClubBoltOnTierIdSecond))) {
                // const ocHeading = document.getElementsByClassName('order-confirmation__heading');
                // if (ocHeading && ocHeading.length) {
                //     const ps = ocHeading[0].getElementsByTagName('p');
                //     if (ps && ps.length) {
                //         ps[0].innerHTML = props.config.boltonsThankyouSubHeading ? props.config.boltonsThankyouSubHeading : 'You’re now supercharged! Your Double Up benefits will begin on 1st October 2024';
                //     }                
                // }
    
                // const detailMsgs = document.getElementsByClassName('club-plus-oc-detail-msg');
                // if (detailMsgs && detailMsgs.length) {
                //     detailMsgs[0].innerHTML = props.config.boltonsDetailMsg ? props.config.boltonsDetailMsg : 'An additional two drink credits will be automatically added to your membership card on the 1st of each month throughout the offer period. See full terms and conditions here: https://www.dobbies.com/club-terms-and-conditions';
                // }
    
                // const descHeading = document.getElementsByClassName('ms-order-confirmation__group-delivery-heading');
                // if (descHeading && descHeading.length) {
                //     descHeading[0].innerHTML = props.config.boltonsDetailMsgHeading ? props.config.boltonsDetailMsgHeading : 'How do I redeem my extra hot drinks';
                // }

                if (products && products.length) {
                    if (products[0].Name && boltonProductTitle !== products[0].Name) {
                        setBoltonProductTitle(products[0]?.Name);
                    }
                }
                // Update Order Summary Product Title
            }
        // }, 500);
    }, []);


    /* VSI Customization - END */
    return (
        <Module {...moduleProps}>
            <div className='row'>
                <div className='col-12'>{orderInfomation && heading}</div>
                <div className='ms-checkout__main'>
                    {alert}
                    {loading}
                    {/* VSI Customization - START - 23/11/20 */}
                    {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} {...salesOrderInfo} {...created} />}
                    {groups && OrderConfirmationGroups(props)}
                    {/* VSI Customization - END */}
                </div>
                <div className='ms-checkout__side'>
                    {orderSummary && (
                        <OrderConfirmationOrderSummary
                            clubPlusTrialText={clubPlusTrialText}
                            formattedPrice={formattedPrice?.toString()}
                            products={products}
                            {...orderSummary}
                            clubPlusText={boltonProductTitle ? boltonProductTitle : clubPlusText}
                        />
                    )}
                    {payment && <OrderConfirmationPayment {...payment} />}
                    {/* VSI Customization - START - 23/11/20 */}
                    {orderInfomation && (
                        <Node className='help-centre-block' tag='div'>
                            {helpCentreBlock}
                        </Node>
                    )}
                    {/* VSI Customization - END */}
                    {help && <OrderConfirmationHelp {...help} />}
                </div>
            </div>
        </Module>
    );
};

export default OrderConfirmationView;
